import { useEffect, useState, useMemo } from "react";
import { API_URL } from "../../../../../constants";
import axios from "axios";
import { useTranslation } from "../../../../../language";
import { useAuth } from "../../../../../provider";
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton,
  ButtonBase,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Info, MousePointerClick, Plus, Minus } from "lucide-react";
import CustomDialog from "../../../../../components/Dialog/DiaCompMedium";
import { LineChart } from "@mui/x-charts";
import {
  ResponsiveContainer,
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ReferenceLine as RReferenceLine,
  Cell,
  LabelList,
} from 'recharts';
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CustomDialogSmall from "../../../../../components/Dialog/DiaCompSmall";
import { Recommendations } from "./Recommendations";
import { DateSelector } from "../../../Cockpit/HealthCockpit";


import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import CloseIcon from '@mui/icons-material/Close';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ─────────────────────────────────────────────────────────────────────────────
// InfoDialog — structured info panel with intro text + accordion sections
// Replaces the flat CustomDialogSmall for all info buttons in PhenoAgeCockpit.
//
// Props:
//   open       boolean
//   onClose    () => void
//   title      string
//   intro      string           — paragraph shown above the accordions
//   sections   Array<{ label: string, content: string, recommendation?: string, defaultOpen?: boolean }>
// ─────────────────────────────────────────────────────────────────────────────
const InfoDialog = ({ open, onClose, title, intro, sections = [] }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [openIdx, setOpenIdx] = useState(
    () => new Set(sections.map((s, i) => (s.defaultOpen ? i : null)).filter((i) => i !== null))
  );

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      title={title}
      content={
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {intro && (
            <Typography variant="body1" sx={{ lineHeight: 1.75, mb: sections.length ? 2 : 0 }}>
              {intro}
            </Typography>
          )}

          {sections.map((sec, i) => {
            const isOpen = openIdx.has(i);
            return (
              <Accordion
                key={i}
                expanded={isOpen}
                onChange={(e, expandedNow) => {
                  setOpenIdx((prev) => {
                    const next = new Set(prev);
                    if (expandedNow) next.add(i); else next.delete(i);
                    return next;
                  });
                }}
                disableGutters
                elevation={0}
                square
                sx={{
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={isOpen
                    ? <Minus size={18} color={theme.palette.mode === 'light' ? theme.palette.greydark.C900 : theme.palette.grey.C50} />
                    : <Plus size={18} color={theme.palette.mode === 'light' ? theme.palette.greydark.C900 : theme.palette.grey.C50} />}
                  sx={{ px: 0 }}
                >
                  <Typography variant="h6" fontWeight={isOpen ? 600 : 400} sx={{ letterSpacing: '0.015em' }}>
                    {sec.label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.75, mb: sec.recommendation ? 1.5 : 0 }}>
                    {sec.content}
                  </Typography>
                  {sec.recommendation && (
                    <Box sx={{
                      p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      bgcolor: theme.palette.mode === 'dark' ? 'greydark.C900' : 'grey.C50',
                    }}>
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>{t("bioage_info_recommendation")}: </Box>
                        {sec.recommendation}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      }
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Types & constants
// ─────────────────────────────────────────────────────────────────────────────

/** @typedef {{ name: string, value: number, actual: number, target: number, unit: string, better: "higher"|"lower" }} Driver */
/** @typedef {{ id: string|number, date: string, bioage: string, agediff: string, mortrisk: string, age_drivers: string|object[] }} Snapshot */

const AGING_RATE_THRESHOLD = 1;
const MORTALITY_RISK_THRESHOLD = 3;

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────────────

const UNIT_CORRECTIONS = {
  "k/cumm": "G/l",
  "K/cumm": "G/l",
  "k/uL":   "G/l",
  "K/uL":   "G/l",
};

/** Safely parse the age_drivers field from any snapshot. Returns [] on failure. */
const parseDrivers = (raw) => {
  try {
    const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(arr)) return [];
    return arr.map((d) => ({
      name: d.display_name,
      value: Number(d.contribution_years),
      actual: Number(d.actual_val),
      target: Number(d.target_val),
      unit: UNIT_CORRECTIONS[d.unit] ?? d.unit ?? "",
      better: d.better,
    }));
  } catch {
    return [];
  }
};

const fmtDate = (iso, locale = "en-GB") =>
  new Date(iso).toLocaleDateString(locale, {
    month: "short",
    year: "numeric",
  });

const signedLabel = (n, decimals = 1) =>
  `${n > 0 ? "+" : ""}${n.toFixed(decimals)}`;

// ─────────────────────────────────────────────────────────────────────────────
// Shared hook: card surface styles driven by theme
// ─────────────────────────────────────────────────────────────────────────────

const useCardStyles = () => {
  const theme = useTheme();
  return useMemo(
    () => ({
      bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
      boxShadow: theme.palette.mode === "dark"
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
      borderRadius: "24px",
      p: { xs: 2.25, sm: 3 },
    }),
    [theme.palette.mode]
  );
};

// Returns the two semantic marker colors from the theme traffic palette.
const useMarkerColors = () => {
  const theme = useTheme();
  return {
    orange: theme.palette.traffic.orange,
    emerald: theme.palette.traffic.green,
    red: theme.palette.traffic.red,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Translation-key helpers (mirrors Recommendations.jsx + TornadoChart.jsx)
// ─────────────────────────────────────────────────────────────────────────────

/** Derive the uppercase suffix used in translation keys from an abbreviation or name. */
const DISPLAY_NAME_TO_KEY = {
  'ALBUMIN':      'ALB',
  'LYMPHOCYTES':  'LYM',
  'HSCRP':        'CRP',
  'CRP':          'CRP',
  'ALK. PHOS':    'ALP',
  'ALK.PHOS':     'ALP',
  'GLUCOSE':      'GLU',
  'CREATININE':   'CRE',
  'RDW':          'RDW',
  'MCV':          'MCV',
  'WBC':          'WBC',
};

const toAbbKey = (abbOrName) => {
  const upper = (abbOrName || '').toUpperCase().trim();
  return DISPLAY_NAME_TO_KEY[upper] ?? upper.replace(/\s+/g, '_').slice(0, 6);
};

/**
 * Enrich a raw driver (from parseDrivers) with translated content.
 * Looks up  marker_desc_<KEY>, what_it_does_<KEY>, action_<KEY>.
 * Falls back to '' so the existing fallback UI still renders.
 */
const useEnrichedMarker = (marker, t) => {
  if (!marker) return null;
  // PhenoAgeCockpit drivers use `name` (display name); TornadoChart uses `abbreviation`
  const key = toAbbKey(marker.abbreviation || marker.name || '');

  const descKey   = `marker_desc_${key}`;
  const whatKey   = `what_it_does_${key}`;
  const actionKey = `action_${key}`;

  return {
    ...marker,
    description:     t(descKey)   !== descKey   ? t(descKey)   : '',
    what_it_does:    t(whatKey)   !== whatKey   ? t(whatKey)   : '',
    how_to_optimize: t(actionKey) !== actionKey ? t(actionKey) : '',
  };
};

const enrichAllMarkers = (markers, t) =>
  markers.map((marker) => {
    const key = toAbbKey(marker.abbreviation || marker.name || '');
    return {
      ...marker,
      description:     t(`marker_desc_${key}`)   !== `marker_desc_${key}`   ? t(`marker_desc_${key}`)   : '',
      what_it_does:    t(`what_it_does_${key}`)   !== `what_it_does_${key}`   ? t(`what_it_does_${key}`)   : '',
      how_to_optimize: t(`action_${key}`)         !== `action_${key}`         ? t(`action_${key}`)         : '',
    };
  });

// Shared card body — used both inside the dialog and as an inline card on the page.
// Pass onClose to show the close button (dialog mode); omit it for inline mode.
const MarkerDetailCard = ({ marker, t, onClose }) => {
  const theme = useTheme();
  const { orange: markerOrange, emerald: markerEmerald, red: markerRed } = useMarkerColors();
  const [openSections, setOpenSections] = useState(new Set());

  if (!marker) return null;

  const impact_years    = marker.value ?? marker.impact_years;
  const isHarmful       = impact_years > 0;
  const accentColor     = isHarmful ? markerRed : markerEmerald;

  const rawName         = marker.name || marker.abbreviation || '—';
  const longNameKey     = `markername_long_${toAbbKey(marker.abbreviation || marker.name || '')}`;
  const displayName     = t(longNameKey) !== longNameKey ? t(longNameKey) : rawName;
  const current_value   = marker.actual  ?? marker.current_value;
  const optimized_value = marker.target  ?? marker.optimized_value;
  const unit            = marker.unit    ?? marker.current_unit ?? '';
  const description     = marker.description    ?? '';
  const what_it_does    = marker.what_it_does   ?? '';
  const how_to_optimize = marker.how_to_optimize ?? '';

  const textSections = [
    {
      key: 'description',
      label: t('tornado_dialog_description_label') !== 'tornado_dialog_description_label' ? t('tornado_dialog_description_label') : 'About this marker',
      content: description,
    },
    { key: 'what_it_does', label: t('tornado_dialog_what_it_does_label'), content: what_it_does },
    { key: 'how_to_optimize', label: t('tornado_dialog_action_label'), content: how_to_optimize, accentColor: markerEmerald },
  ].filter((s) => s.content);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Close button — its own row above the title, not overlapping it */}
      {onClose && (
        <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', px: '12px', pt: '12px' }}>
          <IconButton aria-label="close" onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      {/* Header */}
      <Box sx={{ pt: onClose ? 0 : '8px', px: '24px', pb: 0, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: theme.palette.mode === 'dark' ? 'grey.C50' : 'greydark.C900', fontWeight: 600 }}>
          {displayName}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 400, mt: '4px' }}>
          {t('tornado_dialog_subtitle')}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{
        pt: '20px', pb: '24px', px: '24px', overflowY: 'auto', flex: 1,
      }}>
        {/* Impact badge */}
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          px: '12px', py: '6px', borderRadius: '999px',
          bgcolor: alpha(accentColor, 0.08),
          mb: '10px',
        }}>
          {isHarmful
            ? <TrendingUpIcon sx={{ color: markerOrange, fontSize: 20 }} />
            : <TrendingDownIcon sx={{ color: markerEmerald, fontSize: 20 }} />
          }
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: accentColor }}>
            {impact_years > 0 ? '+' : ''}{impact_years.toFixed(1)} {t('tornado_dialog_years')}
          </Typography>
        </Box>

        {/* Current → Optimized → Savings */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 14px 1fr 14px 1fr', sm: '1fr 20px 1fr 20px 1fr' },
          alignItems: 'center',
          gap: { xs: '4px', sm: '8px' },
          bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.05) : alpha(theme.palette.greydark.C900, 0.035),
          borderRadius: '18px',
          p: { xs: '14px 8px', sm: '18px 16px' },
          mb: '20px',
        }}>
          <CockpitValueCell label={t('tornado_dialog_current')} value={current_value != null ? current_value : '—'} unit={unit} color={accentColor} />
          <ArrowForwardIcon sx={{ color: 'text.disabled', fontSize: { xs: 12, sm: 14 }, mx: 'auto' }} />
          <CockpitValueCell label={t('tornado_dialog_optimized')} value={optimized_value != null ? optimized_value : '—'} unit={unit} color={markerEmerald} />
          <Box sx={{ width: '1px', height: 36, bgcolor: alpha(theme.palette.divider, 0.6), mx: 'auto' }} />
          <CockpitValueCell
            label={t('tornado_dialog_savings')}
            value={impact_years > 0 ? `−${impact_years.toFixed(1)}` : `${Math.abs(impact_years).toFixed(1)}`}
            unit={t('tornado_dialog_years')}
            color={markerEmerald}
            highlight
          />
        </Box>

        {/* Text sections */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {textSections.map((sec) => {
            const isOpen = openSections.has(sec.key);
            const iconColor = sec.accentColor || (theme.palette.mode === 'light' ? theme.palette.greydark.C900 : theme.palette.grey.C50);
            return (
              <Accordion
                key={sec.key}
                expanded={isOpen}
                onChange={(e, expandedNow) => {
                  setOpenSections((prev) => {
                    const next = new Set(prev);
                    if (expandedNow) next.add(sec.key); else next.delete(sec.key);
                    return next;
                  });
                }}
                disableGutters
                elevation={0}
                square
                sx={{
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={isOpen ? <Minus size={18} color={iconColor} /> : <Plus size={18} color={iconColor} />}
                  sx={{ px: 0 }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={isOpen ? 600 : 400}
                    sx={{ letterSpacing: '0.015em', color: sec.accentColor || iconColor }}
                  >
                    {sec.label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.75, color: theme.palette.mode === 'dark' ? 'grey.C50' : 'greydark.C900' }}>
                    {sec.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
          {textSections.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <EmojiEventsOutlinedIcon sx={{ color: 'text.disabled', fontSize: 24, mb: '8px' }} />
              <Typography variant="body2" color="text.disabled">
                {t('tornado_dialog_no_detail')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const MarkerDetailDialog = ({ open, onClose, marker, t }) => {
  const theme = useTheme();
  if (!marker) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.75) : alpha(theme.palette.common.white, 0.25),
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '24px',
          overflow: 'hidden',
          // background.paper defaults to MUI's stock dark value (#121212), a
          // flat neutral grey that doesn't match the app's own blue-tinted
          // greydark scale used everywhere else — greydark.C800 lines it up.
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.greydark.C800 : theme.palette.background.dialog,
          py: 2,
          maxHeight: '85vh',
          margin: { xs: '6px', sm: '32px' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <MarkerDetailCard marker={marker} t={t} onClose={onClose} />
    </Dialog>
  );
};

const CockpitValueCell = ({ label, value, unit, color, highlight }) => {
  const { emerald: markerEmerald } = useMarkerColors();
  return (
    <Box sx={{ textAlign: 'center', minWidth: 0 }}>
      <Typography sx={{
        fontSize: '0.8rem', fontWeight: 600, color: highlight ? markerEmerald : 'text.secondary', mb: '6px', lineHeight: 1.3,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: { xs: '1.15rem', sm: '1.45rem' }, fontWeight: 700,
        color, lineHeight: 1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </Typography>
      {unit && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: '4px' }}>{unit}</Typography>}
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

// ── Card heading — title + quiet info button + subtitle, one style for every
//    card (size "card") and metric tile (size "tile") on the page ─────────────
const CardHeading = ({ title, subtitle, onInfo, infoLabel = "Info", size = "card", onDark = false }) => {
  const theme = useTheme();
  const tile = size === "tile";
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        <Typography component={tile ? "h3" : "h2"} sx={{
          fontSize: tile ? "1rem" : { xs: "1.15rem", sm: "1.3rem" }, fontWeight: 700, lineHeight: 1.25,
          letterSpacing: "-0.01em", color: onDark ? "#fff" : theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900",
        }}>
          {title}
        </Typography>
        {onInfo && (
          <Tooltip title={infoLabel} placement="top">
            <IconButton size="small" aria-label={infoLabel} onClick={onInfo} sx={{ color: onDark ? alpha("#fff", 0.75) : "text.secondary" }}>
              <Info size={tile ? 16 : 18} strokeWidth={2.25} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {subtitle && (
        <Typography sx={{ mt: 0.25, fontSize: tile ? "0.85rem" : "0.95rem", color: onDark ? alpha("#fff", 0.7) : "text.secondary", lineHeight: 1.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

// ── Section header ─────────────────────────────────────────────────────────
const SectionHeader = ({ date, setOpenInfo }) => {
  const { t, language } = useTranslation();
  const locale = language === "de" ? "de-DE" : "en-GB";
  return (
    <CardHeading
      title="PhenoAge"
      subtitle={`${t("age_9").trim()} · ${fmtDate(date, locale)}`}
      onInfo={() => setOpenInfo(true)}
    />
  );
};

// ── Biological age card ────────────────────────────────────────────────────
const BiologicalAgeCard = ({
  bioAge,
  ageDiff,
  chronologicalAge,
  deltaAge,
}) => {
  const improved = deltaAge < 0;
  const {t} = useTranslation()
  const deltaLabel = `${signedLabel(deltaAge)} ${t('tornado_dialog_years')}`;

  const theme = useTheme();
  const [openInfo, setOpenInfo] = useState(false);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardHeading
        size="tile"
        onDark
        title={t("bioage_title_bioage")}
        subtitle={t("bioage_subheading")}
        onInfo={() => setOpenInfo(true)}
        infoLabel={t("bioage_info_tooltip")}
      />
      <Typography sx={{ mt: 0.5, mb: 2, fontSize: "0.85rem", color: alpha("#fff", 0.7), lineHeight: 1.5, fontVariantNumeric: "tabular-nums" }}>
        {t("bioage_chrono_age")}: <Box component="span" sx={{ fontWeight: 600, color: "#fff" }}>{chronologicalAge}</Box>
      </Typography>
      <InfoDialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title={t("bioage_title_bioage")}
        intro={t("bioage_info_intro") !== "bioage_info_intro" ? t("bioage_info_intro") : t("bioage_info_content")}
        sections={[
          { label: t("bioage_section_what_label") !== "bioage_section_what_label" ? t("bioage_section_what_label") : "What is Biological Age?", content: t("bioage_section_what") !== "bioage_section_what" ? t("bioage_section_what") : "", defaultOpen: true },
          { label: t("bioage_section_how_label")  !== "bioage_section_how_label"  ? t("bioage_section_how_label")  : "How is it calculated?",    content: t("bioage_section_how")  !== "bioage_section_how"  ? t("bioage_section_how")  : "" },
          { label: t("bioage_section_interpret_label") !== "bioage_section_interpret_label" ? t("bioage_section_interpret_label") : "How to interpret your result", content: t("bioage_section_interpret") !== "bioage_section_interpret" ? t("bioage_section_interpret") : "", recommendation: t("bioage_section_rec") !== "bioage_section_rec" ? t("bioage_section_rec") : undefined },
        ].filter(s => s.content)}
      />

      <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Box>
          <Typography
            variant="h2"
            sx={{
              color: ageDiff < 0 ? theme.palette.traffic.green : theme.palette.traffic.red,
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {bioAge.toFixed(1)}{" "}
            <Typography component="span" sx={{ fontSize: "1rem", fontWeight: 500, color: alpha("#fff", 0.7), letterSpacing: 0 }}>
              {t("age_11")}
            </Typography>
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={0.75}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {ageDiff < 0
              ? <TrendingDownIcon sx={{ fontSize: 14, color: theme.palette.traffic.green }} />
              : <TrendingUpIcon   sx={{ fontSize: 14, color: theme.palette.traffic.red   }} />}
            <Typography sx={{ fontSize: "0.82rem", color: ageDiff < 0 ? theme.palette.traffic.green : theme.palette.traffic.red, fontWeight: 600, lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }}>
              {signedLabel(ageDiff)} {t("age_13")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {improved
              ? <TrendingDownIcon sx={{ fontSize: 14, color: theme.palette.traffic.green }} />
              : <TrendingUpIcon   sx={{ fontSize: 14, color: theme.palette.traffic.red   }} />}
            <Typography sx={{ fontSize: "0.82rem", color: improved ? theme.palette.traffic.green : theme.palette.traffic.red, fontWeight: 600, lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }}>
              {deltaLabel} {t("age_14")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Pace card ──────────────────────────────────────────────────────────────
const PaceCard = ({ rateOfAging }) => {
    const {t} = useTranslation()
    const [openInfo, setOpenInfo] = useState(false);

return(

  <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <Box sx={{ mb: 2 }}>
      <CardHeading
        size="tile"
        title={t("bioage_title_pace")}
        subtitle={t("pace_subheading")}
        onInfo={() => setOpenInfo(true)}
        infoLabel={t("pace_info_tooltip")}
      />
    </Box>
    <InfoDialog
      open={openInfo}
      onClose={() => setOpenInfo(false)}
      title={t("bioage_title_pace")}
      intro={t("pace_info_intro") !== "pace_info_intro" ? t("pace_info_intro") : t("pace_info_content")}
      sections={[
        { label: t("pace_section_what_label") !== "pace_section_what_label" ? t("pace_section_what_label") : "What does it mean?", content: t("pace_section_what") !== "pace_section_what" ? t("pace_section_what") : "", defaultOpen: true },
        { label: t("pace_section_optimal_label") !== "pace_section_optimal_label" ? t("pace_section_optimal_label") : "Optimal range", content: t("pace_section_optimal") !== "pace_section_optimal" ? t("pace_section_optimal") : "", recommendation: t("pace_section_rec") !== "pace_section_rec" ? t("pace_section_rec") : undefined },
        { label: t("pace_section_accelerated_label") !== "pace_section_accelerated_label" ? t("pace_section_accelerated_label") : "Accelerated aging", content: t("pace_section_accelerated") !== "pace_section_accelerated" ? t("pace_section_accelerated") : "", recommendation: t("pace_section_accelerated_rec") !== "pace_section_accelerated_rec" ? t("pace_section_accelerated_rec") : undefined },
      ].filter(s => s.content)}
    />
    <Typography
      variant="h2"
      sx={{
        mt: "auto",
        color: rateOfAging <= AGING_RATE_THRESHOLD ? "success.main" : "error.main",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "-0.02em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {rateOfAging}{" "}
      <Typography component="span" sx={{ fontSize: "1rem", fontWeight: 500, color: "text.secondary", letterSpacing: 0 }}>
        x
      </Typography>
    </Typography>
  </Box>
)};

// ── Mortality card ─────────────────────────────────────────────────────────
const MortalityCard = ({ mortRisk }) => {
    const {t} = useTranslation()
    const [openInfo, setOpenInfo] = useState(false);

return(
  <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <Box sx={{ mb: 2 }}>
      <CardHeading
        size="tile"
        title={t("bioage_title_mortality")}
        subtitle={t("mortality_subheading")}
        onInfo={() => setOpenInfo(true)}
        infoLabel={t("mortality_info_tooltip")}
      />
    </Box>
    <InfoDialog
      open={openInfo}
      onClose={() => setOpenInfo(false)}
      title={t("bioage_title_mortality")}
      intro={t("mortality_info_intro") !== "mortality_info_intro" ? t("mortality_info_intro") : t("mortality_info_content")}
      sections={[
        { label: t("mortality_section_what_label") !== "mortality_section_what_label" ? t("mortality_section_what_label") : "What is 10-year mortality risk?", content: t("mortality_section_what") !== "mortality_section_what" ? t("mortality_section_what") : "", defaultOpen: true },
        { label: t("mortality_section_low_label") !== "mortality_section_low_label" ? t("mortality_section_low_label") : "Low risk (< 3%)", content: t("mortality_section_low") !== "mortality_section_low" ? t("mortality_section_low") : "", recommendation: t("mortality_section_low_rec") !== "mortality_section_low_rec" ? t("mortality_section_low_rec") : undefined },
        { label: t("mortality_section_elevated_label") !== "mortality_section_elevated_label" ? t("mortality_section_elevated_label") : "Elevated risk (≥ 3%)", content: t("mortality_section_elevated") !== "mortality_section_elevated" ? t("mortality_section_elevated") : "", recommendation: t("mortality_section_elevated_rec") !== "mortality_section_elevated_rec" ? t("mortality_section_elevated_rec") : undefined },
      ].filter(s => s.content)}
    />
    <Typography
      variant="h2"
      sx={{
        mt: "auto",
        color:
          mortRisk < MORTALITY_RISK_THRESHOLD ? "success.main" : "error.main",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "-0.02em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {mortRisk}{" "}
      <Typography component="span" sx={{ fontSize: "1rem", fontWeight: 500, color: "text.secondary", letterSpacing: 0 }}>
        %
      </Typography>
    </Typography>
  </Box>
)};

// ── Metrics section — the three metrics side by side ─────────────────────
const MetricsSection = ({
  bioAge,
  ageDiff,
  mortRisk,
  rateOfAging,
  chronologicalAge,
  deltaAge,
}) => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  // A tone step on the card surface, no border or shadow
  const tileSx = {
    minWidth: 0,
    p: { xs: 2, sm: 2.5 },
    borderRadius: "18px",
    bgcolor: dark ? alpha("#fff", 0.05) : alpha("#fff", 0.6),
  };

  return (
    <Box sx={{
      mt: 2,
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
      gap: 1.5,
    }}>
      <Box sx={{
        ...tileSx,
        // Dark (greydark) surface, as the BioAge tile on the Health Cockpit
        bgcolor: theme.palette.greydark.C900,
        border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : alpha(theme.palette.greydark.C900, 0.9)}`,
        boxShadow: dark
          ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
          : "inset 0 1px 0 rgba(255,255,255,0.08), 0 6px 24px rgba(0,0,0,0.18)",
      }}>
        <BiologicalAgeCard
          bioAge={bioAge}
          ageDiff={ageDiff}
          chronologicalAge={chronologicalAge}
          deltaAge={deltaAge}
        />
      </Box>
      <Box sx={tileSx}>
        <PaceCard rateOfAging={rateOfAging} />
      </Box>
      <Box sx={tileSx}>
        <MortalityCard mortRisk={mortRisk} />
      </Box>
    </Box>
  );
};

// ── TornadoBarChart — Recharts diverging horizontal bar chart ───────────────
const TornadoBarChart = ({ drivers, markerFullName, onBarClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { red: markerRed, emerald: markerEmerald } = useMarkerColors();

  const maxAbsVal = Math.max(...drivers.map(d => Math.abs(d.value)));
  const domainMax = Math.ceil(maxAbsVal + 0.5);
  const chartHeight = Math.max(320, drivers.length * 46 + 72);

  const chartData = useMemo(
    () => drivers.map(d => ({
      name: markerFullName(d.name),
      value: parseFloat(d.value.toFixed(2)),
      driver: d,
    })),
    [drivers, markerFullName],
  );

  return (
    <Box>
    <ResponsiveContainer width="100%" height={chartHeight}>
      <RBarChart
        data={chartData}
        layout="vertical"
        margin={{ top: 16, right: isMobile ? 84 : 100, left: isMobile ? 4 : 12, bottom: 36 }}
        barCategoryGap={8}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={theme.palette.divider} />
        <XAxis
          type="number"
          domain={[-domainMax, domainMax]}
          tickFormatter={v => v > 0 ? `+${v}` : String(v)}
          tick={{ fontSize: 11, fill: theme.palette.text.secondary, fontVariantNumeric: 'tabular-nums' }}
          axisLine={{ stroke: theme.palette.divider }}
          tickLine={false}
          label={{ value: t('tornado_impact_years'), position: 'insideBottom', offset: -12, fontSize: 11.5, fontFamily: "'Helvetica Neue', sans-serif", fill: theme.palette.text.primary }}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={isMobile ? 80 : 88}
          tick={{ fontSize: 11.5, fill: theme.palette.text.primary }}
          axisLine={false}
          tickLine={false}
        />
        <RReferenceLine x={0} stroke={theme.palette.divider} strokeWidth={1.5} />
        <RTooltip
          formatter={(value) => [`${value > 0 ? '+' : ''}${value.toFixed(1)} ${t('age_11')}`, t('tornado_impact_years')]}
          contentStyle={{
            borderRadius: 10,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
            fontSize: 12,
          }}
          cursor={{ fill: theme.palette.action.hover }}
        />
        <Bar
          dataKey="value"
          cursor="pointer"
          barSize={16}
          radius={3}
          onClick={(data) => onBarClick(data.driver)}
        >
          {chartData.map((entry, i) => (
            <Cell key={i} fill={entry.value > 0 ? markerRed : markerEmerald} />
          ))}
          <LabelList
            dataKey="value"
            content={({ x, y, width, height, value }) => {
              if (value == null) return null;
              const isPos = value > 0;
              const absW = Math.abs(width);
              return (
                <text
                  x={isPos ? x + absW + 4 : x + 4}
                  y={y + height / 2}
                  textAnchor="start"
                  dominantBaseline="middle"
                  fontSize={11.5}
                  fontWeight={700}
                  fill={value > 0 ? markerRed : markerEmerald}
                  fontFamily="'Helvetica Neue', sans-serif"
                >
                  {`${isPos ? '+' : ''}${Math.abs(value).toFixed(1)}`}
                </text>
              );
            }}
          />
        </Bar>
      </RBarChart>
    </ResponsiveContainer>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: markerRed }} />
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 400 }}>
          {t('age_18')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: markerEmerald }} />
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 400 }}>
          {t('age_19')}
        </Typography>
      </Box>
    </Box>
    </Box>
  );
};


// ── Aging drivers section ──────────────────────────────────────────────────
const AgingDriversSection = ({ drivers, cardStyles, allData, selected, onSelect, chronologicalAge }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { red: markerRed, emerald: markerEmerald } = useMarkerColors();
  const [rawSelectedMarker, setRawSelectedMarker] = useState(null);
  const [openInfo, setOpenInfo] = useState(false);
  const [expandedMarkers, setExpandedMarkers] = useState(new Set());

  const enrichedMarker  = useEnrichedMarker(rawSelectedMarker, t);
  const enrichedDrivers = useMemo(() => enrichAllMarkers(drivers, t), [drivers, t]);

  const markerFullName = (name) => {
    const key = `markername_short_${toAbbKey(name)}`;
    const translated = t(key);
    return translated !== key ? translated : name;
  };

  // Top 3 positive drivers (adds years → most impactful to optimize)
  const topDrivers = useMemo(
    () => drivers.filter((d) => d.value > 0).slice(0, 3),
    [drivers],
  );

  return (
    <>
      <Grid size={12}>
        <Card variant="outlined" sx={{ ...cardStyles, overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ mb: 1 }}>
            <CardHeading
              title={t('age_17')}
              subtitle={t("drivers_subheading")}
              onInfo={() => setOpenInfo(true)}
              infoLabel={t("drivers_info_tooltip")}
            />
          </Box>
          <InfoDialog
            open={openInfo}
            onClose={() => setOpenInfo(false)}
            title={t("age_17")}
            intro={t("drivers_info_intro") !== "drivers_info_intro" ? t("drivers_info_intro") : t("drivers_info_content")}
            sections={[
              { label: t("drivers_section_what_label") !== "drivers_section_what_label" ? t("drivers_section_what_label") : "What are aging drivers?", content: t("drivers_section_what") !== "drivers_section_what" ? t("drivers_section_what") : "", defaultOpen: true },
              { label: t("drivers_section_positive_label") !== "drivers_section_positive_label" ? t("drivers_section_positive_label") : "Positive contributors (add years)", content: t("drivers_section_positive") !== "drivers_section_positive" ? t("drivers_section_positive") : "", recommendation: t("drivers_section_positive_rec") !== "drivers_section_positive_rec" ? t("drivers_section_positive_rec") : undefined },
              { label: t("drivers_section_negative_label") !== "drivers_section_negative_label" ? t("drivers_section_negative_label") : "Negative contributors (subtract years)", content: t("drivers_section_negative") !== "drivers_section_negative" ? t("drivers_section_negative") : "" },
              { label: t("drivers_section_action_label") !== "drivers_section_action_label" ? t("drivers_section_action_label") : "What can I do?", content: t("drivers_section_action") !== "drivers_section_action" ? t("drivers_section_action") : "", recommendation: t("drivers_section_action_rec") !== "drivers_section_action_rec" ? t("drivers_section_action_rec") : undefined },
            ].filter(s => s.content)}
          />

          {/* Recharts tornado chart */}
          <TornadoBarChart
            drivers={drivers}
            markerFullName={markerFullName}
            onBarClick={setRawSelectedMarker}
          />

          {/* Top drivers — ranking */}
          {topDrivers.length > 0 && (() => {
            const sorted = [...topDrivers].sort((a, b) => b.value - a.value);
            const maxImpact = sorted[0].value;
            return (
              <Box sx={{ mt: 4 }}>
                {/* Section label */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mb: 1 }}>
                  <Typography component="h3" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.3, color: theme.palette.mode === 'dark' ? 'grey.C50' : 'greydark.C900' }}>
                    {t('tornado_top_drivers')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MousePointerClick size={14} color={theme.palette.text.secondary} />
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {t('tornado_click_hint')}
                    </Typography>
                  </Box>
                </Box>

                {/* Rank rows — bleed to the card edges */}
                <Box sx={{ mx: { xs: -2.25, sm: -3 }, mb: { xs: -2.25, sm: -3 } }}>
                  {sorted.map((d, i) => {
                    const barPct = `${Math.round((d.value / maxImpact) * 100)}%`;
                    const isLast = i === sorted.length - 1;

                    return (
                      <ButtonBase
                        key={i}
                        onClick={() => setRawSelectedMarker(d)}
                        sx={{
                          display: 'flex',
                          width: '100%',
                          alignItems: 'center',
                          gap: 1.5,
                          px: { xs: 2.25, sm: 3 },
                          py: 1.5,
                          pb: isLast ? 2.25 : 1.5,
                          textAlign: 'left',
                          position: 'relative',
                          overflow: 'hidden',
                          borderTop: i > 0 ? '1px solid' : 'none',
                          borderTopColor: 'divider',
                          borderBottomLeftRadius: isLast ? '24px' : 0,
                          borderBottomRightRadius: isLast ? '24px' : 0,
                          transition: 'background-color 150ms ease',
                          '& .bio-row-arrow': { transition: 'transform 200ms cubic-bezier(0.23, 1, 0.32, 1)' },
                          '@media (hover: hover) and (pointer: fine)': {
                            '&:hover': { bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900' },
                            '&:hover .bio-row-arrow': { transform: 'translateX(3px)' },
                          },
                          '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${theme.palette.tertiary.C300}` },
                        }}
                      >
                        {/* Proportional fill behind row */}
                        <Box sx={{
                          position: 'absolute',
                          top: 0, left: 0, bottom: 0,
                          width: barPct,
                          bgcolor: alpha(markerRed, 0.045),
                          pointerEvents: 'none',
                        }} />

                        {/* Rank — step badge (design-system pattern) */}
                        <Box sx={{
                          flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                          border: `2px solid ${markerRed}`, position: 'relative',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Typography variant="number4" sx={{ color: markerRed, fontWeight: 700, fontSize: '0.75rem', lineHeight: 1 }}>
                            {i + 1}
                          </Typography>
                        </Box>

                        {/* Marker info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {markerFullName(d.name)}
                          </Typography>
                          {(d.actual != null || d.target != null) && (
                            <Typography sx={{
                              fontSize: '0.8rem',
                              color: 'text.secondary',
                              lineHeight: 1.4,
                              mt: 0.25,
                              fontVariantNumeric: 'tabular-nums',
                            }}>
                              {d.actual != null ? `${d.actual}${d.unit ? ` ${d.unit}` : ''}` : '—'}
                              {d.target != null ? ` → ${d.target}${d.unit ? ` ${d.unit}` : ''}` : ''}
                            </Typography>
                          )}
                        </Box>

                        {/* Impact */}
                        <Typography sx={{
                          fontSize: '0.95rem',
                          fontWeight: 700,
                          color: markerRed,
                          lineHeight: 1,
                          flexShrink: 0,
                          position: 'relative',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {`+${d.value.toFixed(1)} ${t('age_11')}`}
                        </Typography>

                        <ChevronRightIcon className="bio-row-arrow" sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
            );
          })()}
        </Card>
      </Grid>

      <MarkerDetailDialog
        open={Boolean(rawSelectedMarker)}
        onClose={() => setRawSelectedMarker(null)}
        marker={enrichedMarker}
        t={t}
      />

      {/* Trend chart (only when >1 snapshot) */}
      {allData.length > 1 && (
        <Grid size={12}>
          <BioAgeTrendCard
            allData={allData}
            selected={selected}
            onSelect={onSelect}
            chronologicalAge={chronologicalAge}
          />
        </Grid>
      )}

      {/* Inline driver detail cards */}
      <Grid size={12} sx={{ mt: 3 }}>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ px: 0.5 }}>
            <CardHeading
              title={t('drivers_detail_section_label') !== 'drivers_detail_section_label'
                ? t('drivers_detail_section_label')
                : 'Marker details'}
            />
          </Box>
          {enrichedDrivers.map((driver, i) => {
            const impactYears = driver.value ?? driver.impact_years;
            const isHarmful   = impactYears > 0;
            const impactColor = isHarmful ? theme.palette.traffic.red : theme.palette.traffic.green;
            const rawName = driver.name || driver.abbreviation || '—';
            const markerKey = toAbbKey(driver.abbreviation || driver.name || '');
            const shortNameKey = `markername_short_${markerKey}`;
            const longNameKey  = `markername_long_${markerKey}`;
            const displayName = t(shortNameKey) !== shortNameKey
              ? t(shortNameKey)
              : (t(longNameKey) !== longNameKey ? t(longNameKey) : rawName);
            const isExpanded = expandedMarkers.has(i);

            return (
              <Box
                key={i}
                sx={{
                  ...cardStyles,
                  p: 0,
                  overflow: 'hidden',
                }}
              >
                <Accordion
                  disableGutters
                  elevation={0}
                  expanded={isExpanded}
                  onChange={(e, expandedNow) => {
                    setExpandedMarkers((prev) => {
                      const next = new Set(prev);
                      if (expandedNow) next.add(i); else next.delete(i);
                      return next;
                    });
                  }}
                  sx={{
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    position: 'relative',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
                    sx={{
                      px: { xs: 2.25, sm: 3 },
                      minHeight: 56,
                      bgcolor: 'transparent',
                      '& .MuiAccordionSummary-content': { my: 0.75, display: 'flex', alignItems: 'center', gap: 1.5, pr: 1 },
                    }}
                  >
                    {/* Name — hidden while expanded, since MarkerDetailCard already shows the full name */}
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {isExpanded ? '' : displayName}
                    </Typography>

                    {/* Impact — hidden while expanded, since MarkerDetailCard already shows it */}
                    {impactYears != null && !isExpanded && (
                      <Typography variant="number3" sx={{ fontWeight: 700, color: impactColor, flexShrink: 0 }}>
                        {`${impactYears > 0 ? '+' : ''}${Number(impactYears).toFixed(1)} ${t('age_11')}`}
                      </Typography>
                    )}
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    <MarkerDetailCard marker={driver} t={t} />
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })}
        </Box>
      </Grid>
    </>
  );
};


// ── Bio age trend card ─────────────────────────────────────────────────────
const BioAgeTrendCard = ({
  allData,
  selected,
  onSelect,
  chronologicalAge,
}) => {

  const { t, language } = useTranslation()
  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const theme = useTheme();
  const cardStyles = useCardStyles();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openInfo, setOpenInfo] = useState(false);
  const { red: markerRed } = useMarkerColors();

  const chartDates = useMemo(
    () => allData.map((d) => new Date(d.date)),
    [allData]
  );

  // Small symmetric breathing room so the first/last points don't sit flush against the axes
  const [axisMin, axisMax] = useMemo(() => {
    const first = chartDates[0];
    const last  = chartDates[chartDates.length - 1];
    const span  = last - first;
    const pad   = span > 0 ? span * 0.08 : 1000 * 60 * 60 * 24 * 14; // ~2 weeks fallback
    return [new Date(first.getTime() - pad), new Date(last.getTime() + pad)];
  }, [chartDates]);

  const xAxisFormatter = useMemo(() => {
    const labels = chartDates.map(d =>
      d.toLocaleDateString(locale, { month: "short", year: "numeric" })
    );
    const hasDuplicateMonths = labels.length !== new Set(labels).size;
    return (d) =>
      d.toLocaleDateString(locale,
        hasDuplicateMonths
          ? { day: "numeric", month: "short", year: "numeric" }
          : { month: "short", year: "numeric" }
      );
  }, [chartDates, locale]);
  const bioAges = useMemo(
    () => allData.map((d) => parseFloat(d.bioage)),
    [allData]
  );
  const chronoLine = useMemo(
    () => allData.map(() => chronologicalAge),
    [allData, chronologicalAge]
  );

  const deltaAge   = bioAges.at(-1) - bioAges[0];
  const deltaLabel = `${signedLabel(deltaAge)} ${t('tornado_dialog_years')}`;
  const improved   = deltaAge < 0;

  return (
    <Card variant="outlined" sx={{ ...cardStyles, overflow: 'hidden', width: '100%' }}>
      <Box>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <CardHeading
              title={t("age_20")}
              subtitle={t("trend_subheading")}
              onInfo={() => setOpenInfo(true)}
              infoLabel={t("trend_info_tooltip")}
            />
            <InfoDialog
              open={openInfo}
              onClose={() => setOpenInfo(false)}
              title={t("age_20")}
              intro={t("trend_info_intro") !== "trend_info_intro" ? t("trend_info_intro") : t("trend_info_content")}
              sections={[
                { label: t("trend_section_what_label") !== "trend_section_what_label" ? t("trend_section_what_label") : "What does the trend show?", content: t("trend_section_what") !== "trend_section_what" ? t("trend_section_what") : "", defaultOpen: true },
                { label: t("trend_section_improving_label") !== "trend_section_improving_label" ? t("trend_section_improving_label") : "Improving trend", content: t("trend_section_improving") !== "trend_section_improving" ? t("trend_section_improving") : "", recommendation: t("trend_section_improving_rec") !== "trend_section_improving_rec" ? t("trend_section_improving_rec") : undefined },
                { label: t("trend_section_worsening_label") !== "trend_section_worsening_label" ? t("trend_section_worsening_label") : "Worsening trend", content: t("trend_section_worsening") !== "trend_section_worsening" ? t("trend_section_worsening") : "", recommendation: t("trend_section_worsening_rec") !== "trend_section_worsening_rec" ? t("trend_section_worsening_rec") : undefined },
              ].filter(s => s.content)}
            />
          </Box>

          <Box display="flex" gap={1.5} alignItems="center">
            <Chip
              icon={
                improved ? (
                  <TrendingDownIcon fontSize="small" />
                ) : (
                  <TrendingUpIcon fontSize="small" />
                )
              }
              label={`${deltaLabel} ${t("age_14")}`}
              color={improved ? "success" : "error"}
              variant="outlined"
              size="small"
              sx={{
                borderWidth: '1px',
                height: 30,
                fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.77rem', xl: '0.95rem' },
                '& .MuiChip-icon': { ml: 1 },
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
            <Chip
              icon={<CalendarTodayOutlinedIcon fontSize="small" />}
              label={t("bioage_snapshots").replace("{n}", allData.length)}
              variant="outlined"
              size="small"
              sx={{
                borderWidth: '1px',
                height: 30,
                fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.77rem', xl: '0.95rem' },
                '& .MuiChip-icon': { ml: 1 },
                '& .MuiChip-label': { px: 1.25 },
              }}
            />
          </Box>
        </Box>

        <Box height={280}>
          <LineChart
            xAxis={[{
              data: chartDates,
              scaleType: "time",
              min: axisMin,
              max: axisMax,
              tickInterval: chartDates,
              valueFormatter: xAxisFormatter,
              tickLabelStyle: { fontSize: 11 },
            }]}
            series={[
              {
                data: bioAges,
                label: t("bioage_title_bioage"),
                color: isDark ? markerRed : alpha(markerRed, 0.5),
                showMark: true,
                valueFormatter: (v) => `${v?.toFixed(1)} ${t("age_11")}`,
              },
              {
                data: chronoLine,
                label: `${t("bioage_chrono_age")} (${chronologicalAge})`,
                color: isDark ? theme.palette.text.secondary : theme.palette.text.disabled,
                showMark: false,
                curve: "linear",
              },
            ]}
            onAxisClick={(_event, data) => {
              if (data?.dataIndex != null) onSelect(allData[data.dataIndex]);
            }}
            margin={{ top: 20, bottom: 26, left: isMobile ? 40 : 56, right: isMobile ? 16 : 24 }}
            grid={{ horizontal: true }}
            slotProps={{ legend: { position: { vertical: 'bottom', horizontal: 'middle' }, sx: { marginTop: 0 } } }}
          />
        </Box>

        {/* Snapshot pills — pick a measurement */}
        <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
          {allData.map((snap) => {
            const age      = parseFloat(snap.bioage);
            const diff     = parseFloat(snap.agediff);
            const isActive = snap.date === selected.date;

            return (
              <ButtonBase
                key={snap.id}
                onClick={() => onSelect(snap)}
                aria-pressed={isActive}
                sx={{
                  flexDirection: "column", alignItems: "flex-start",
                  px: 1.5, py: 0.875,
                  borderRadius: "14px",
                  border: "1.5px solid",
                  borderColor: isActive
                    ? (isDark ? theme.palette.grey.C50 : theme.palette.greydark.C900)
                    : alpha(isDark ? theme.palette.common.white : theme.palette.common.black, 0.12),
                  bgcolor: isActive ? alpha(isDark ? theme.palette.common.white : theme.palette.greydark.C900, 0.06) : "transparent",
                  transition: "background-color 150ms ease, border-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                  "@media (hover: hover) and (pointer: fine)": {
                    "&:hover": { bgcolor: alpha(isDark ? theme.palette.common.white : theme.palette.greydark.C900, 0.04) },
                  },
                  "&:active": { transform: "scale(0.97)" },
                  "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.tertiary.C300}`, outlineOffset: 2 },
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.3 }}>
                  {fmtDate(snap.date, locale)}
                </Typography>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, lineHeight: 1.3, fontVariantNumeric: "tabular-nums", color: diff < 0 ? "success.main" : "error.main" }}>
                  {age.toFixed(1)} {t("age_11")}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PhenoAgeCockpit
 *
 * Displays biological age metrics, aging drivers, and trend data fetched from
 * the /phenoages/ endpoint. Allows switching between lab visit snapshots.
 *
 * @param {{ chronologicalAge?: number }} props
 */
const InfoSection = ({ title, children }) => {
  const theme = useTheme();
  return (
    <Accordion
      elevation={0}
      disableGutters
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px !important',
        mb: 1,
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '0 0 8px 0' }
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.primary' }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.85rem' }}>
          {children}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
};

const PhenoAgeCockpit = () => {
  const { t }      = useTranslation();
  const { state } = useAuth()
  const chronologicalAge = state.user.age
  const cardStyles = useCardStyles();

  const [allData,   setAllData]   = useState([]);
  const [selected,  setSelected]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [openInfo,  setOpenInfo]  = useState(false);

  // ── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const fetchBioAge = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/phenoages/`);
        if (cancelled) return;

        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort(
            (a, b) => new Date(a.date) - new Date(b.date)
          );
          setAllData(sorted);
          setSelected(sorted[sorted.length - 1]); // default → most recent
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail ?? "Failed to load bio age data");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBioAge();
    return () => { cancelled = true; };
  }, []);

  // ── Derived values ────────────────────────────────────────────────────────
  const { bioAge, ageDiff, mortRisk, rateOfAging, drivers, deltaAge } =
    useMemo(() => {
      if (!selected) {
        return {
          bioAge: 0, ageDiff: 0, mortRisk: 0, rateOfAging: "0.00",
          drivers: [], deltaAge: 0,
        };
      }

      const bioAge      = parseFloat(selected.bioage);
      const ageDiff     = parseFloat(selected.agediff);
      const mortRisk    = parseFloat(selected.mortrisk);
      const rateOfAging = (bioAge / chronologicalAge).toFixed(2);
      const drivers     = parseDrivers(selected.age_drivers).sort(
        (a, b) => b.value - a.value
      );

      const bioAges  = allData.map((d) => parseFloat(d.bioage));
      const deltaAge = bioAges.length > 1
        ? bioAges.at(-1) - bioAges[0]
        : 0;

      return { bioAge, ageDiff, mortRisk, rateOfAging, drivers, deltaAge };
    }, [selected, allData, chronologicalAge]);

  // ── Render guards ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!selected) return null;

  // ── Layout ────────────────────────────────────────────────────────────────
  return (
    <>
    <Grid
      container
      spacing={2}
      sx={{
        mb: 4,
        width: "100%",
        maxWidth: '900px',
        mx: "auto",
        px: { xs: 2, sm: 4, md: 6 },
      }}
    >
      {/* Date selector */}
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <DateSelector
          dates={allData.map((d) => d.date).reverse()}
          selectedDate={selected.date}
          onSelect={(date) => setSelected(allData.find((d) => d.date === date))}
        />
      </Grid>

      {/* Header + Metrics in one large card */}
      <Grid size={12}>
        <Card
          data-tour="bioage-chart"
          variant="outlined"
          sx={{ ...cardStyles, overflow: 'hidden' }}
        >
          <SectionHeader date={selected.date} setOpenInfo={setOpenInfo} />
          <MetricsSection
            bioAge={bioAge}
            ageDiff={ageDiff}
            mortRisk={mortRisk}
            rateOfAging={rateOfAging}
            chronologicalAge={chronologicalAge}
            deltaAge={deltaAge}
          />
        </Card>
      </Grid>

      {/* Aging drivers chart, trend card, and per-marker detail cards */}
      {drivers.length > 0 && (
        <AgingDriversSection
          drivers={drivers}
          cardStyles={cardStyles}
          allData={allData}
          selected={selected}
          onSelect={setSelected}
          chronologicalAge={chronologicalAge}
        />
      )}



    </Grid>

    {/* Info dialog — rendered outside the Grid so it's not clipped */}
    <InfoDialog
      open={openInfo}
      onClose={() => setOpenInfo(false)}
      title="PhenoAge"
      intro={t("age_3")}
      sections={[
        { label: t("age_4_label") !== "age_4_label" ? t("age_4_label") : "The algorithm", content: t("age_4"), defaultOpen: true },
        { label: t("age_5_label") !== "age_5_label" ? t("age_5_label") : "Why it matters", content: t("age_5") },
        { label: t("age_6_label") !== "age_6_label" ? t("age_6_label") : "Research evidence", content: t("age_6") },
        { label: t("age_7_label") !== "age_7_label" ? t("age_7_label") : "What you can do", content: t("age_7"), recommendation: t("age_8") !== "age_8" ? t("age_8") : undefined },
      ].filter(s => s.content && s.content !== s.label)}
    />
  </>
  );
};

export default PhenoAgeCockpit;
